import {
  InteractionEvent,
  SignInIdentifier,
  type VerificationCodeIdentifier,
} from '@logto/schemas';
import classNames from 'classnames';
import { HTTPError } from 'ky';
import { LoaderCircle } from 'lucide-react';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTimer } from 'react-timer-hook';

import CaptchaContext from '@/Providers/CaptchaContextProvider/CaptchaContext';
import { identifyWithVerificationCode, registerWithVerifiedIdentifier } from '@/apis/experience';
import { sendVerificationCodeApi } from '@/apis/utils';
import CaptchaBox from '@/containers/CaptchaBox';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import useTerms from '@/hooks/use-terms';
import EvaAudioLogo from '@/shared/assets/eva-audio-logo.svg?react';
import { UserFlow } from '@/types';
import { sendWithSmsBypass } from '@/utils/sms-bypass';

import EvaAgreement from '../EvaAgreement';
import EvaInput from '../EvaInput';

import styles from './index.module.scss';
import { isValidEvaPhoneNumber } from './phone-validation';

const phoneCountryCallingCode = '86';
const verificationCodeLength = 6;
const resendSeconds = 59;
const agreementShakeDuration = 400;

const getTimerExpiry = () => {
  const now = new Date();
  now.setSeconds(now.getSeconds() + resendSeconds);

  return now;
};

const getErrorCode = async (error: unknown) => {
  if (error instanceof HTTPError) {
    const body: unknown = await error.response.clone().json();

    if (body && typeof body === 'object' && 'code' in body && typeof body.code === 'string') {
      return body.code;
    }
  }
};

/** 从错误 data 里取 retryAfter 秒数;形状不对时返回 undefined,由调用方走默认倒计时 */
const getRetryAfterSeconds = (data: unknown) => {
  if (data && typeof data === 'object' && 'retryAfter' in data) {
    const retryAfter = Number(data.retryAfter);

    if (Number.isFinite(retryAfter) && retryAfter > 0) {
      return retryAfter;
    }
  }
};

const EvaPhoneCodeSignIn = ({ isEmbedded = false }: { readonly isEmbedded?: boolean }) => {
  const { t } = useTranslation();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [verificationId, setVerificationId] = useState<string>();
  const [errorMessage, setErrorMessage] = useState('');
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAgreementShaking, setIsAgreementShaking] = useState(false);

  const { executeCaptcha } = useContext(CaptchaContext);
  const { termsAgreement, isTermsDisabled } = useTerms();
  const handleError = useErrorHandler();
  const redirectTo = useGlobalRedirectTo();
  const sendVerificationCode = useApi(sendVerificationCodeApi);
  const identifyWithCode = useApi(identifyWithVerificationCode);
  const registerWithIdentifier = useApi(registerWithVerifiedIdentifier);

  const identifier = useMemo<VerificationCodeIdentifier>(
    () => ({
      type: SignInIdentifier.Phone,
      value: `${phoneCountryCallingCode}${phone}`,
    }),
    [phone]
  );

  const { seconds, isRunning, restart } = useTimer({
    autoStart: false,
    expiryTimestamp: getTimerExpiry(),
  });

  const validateAgreement = useCallback(async () => {
    if (isTermsDisabled || termsAgreement) {
      return true;
    }

    setIsAgreementShaking(true);
    window.setTimeout(() => {
      setIsAgreementShaking(false);
    }, agreementShakeDuration);

    return false;
  }, [isTermsDisabled, termsAgreement]);

  const validatePhoneInput = useCallback(() => {
    if (!isValidEvaPhoneNumber(phone)) {
      setErrorMessage(t('eva_domestic.invalid_phone'));
      return false;
    }

    return true;
  }, [phone, t]);

  const handleSendCode = useCallback(async () => {
    setErrorMessage('');

    if (!validatePhoneInput()) {
      return;
    }

    setIsSendingCode(true);

    try {
      const [error, result] = await sendWithSmsBypass(executeCaptcha, async (captchaToken) =>
        sendVerificationCode(UserFlow.SignIn, identifier, InteractionEvent.SignIn, captchaToken)
      );

      if (error) {
        await handleError(error, {
          'guard.invalid_input': () => {
            setErrorMessage(t('eva_domestic.invalid_phone'));
          },
          // 服务端重发冷却命中：按服务端 retryAfter 重启倒计时（服务端权威），并提示文案
          'verification_code.rate_limited': (error) => {
            const retryAfter = getRetryAfterSeconds(error.data);
            if (retryAfter) {
              const expiry = new Date();
              expiry.setSeconds(expiry.getSeconds() + retryAfter);
              restart(expiry, true);
            }
            setErrorMessage(error.message);
          },
          global: (error) => {
            setErrorMessage(error.message);
          },
        });

        return;
      }

      if (result) {
        setVerificationId(result.verificationId);
        restart(getTimerExpiry(), true);
      }
    } catch {
      setErrorMessage(t('eva_domestic.verification_failed'));
    } finally {
      setIsSendingCode(false);
    }
  }, [
    executeCaptcha,
    handleError,
    identifier,
    restart,
    sendVerificationCode,
    validatePhoneInput,
    t,
  ]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setErrorMessage('');

      if (!(await validateAgreement()) || !validatePhoneInput()) {
        return;
      }

      if (!verificationId) {
        setErrorMessage(t('eva_domestic.request_code_first'));
        return;
      }

      if (code.length !== verificationCodeLength) {
        setErrorMessage(t('eva_domestic.invalid_code'));
        return;
      }

      setIsSubmitting(true);

      const [signInError, signInResult] = await identifyWithCode({
        verificationId,
        identifier,
        code,
      });

      if (signInResult?.redirectTo) {
        await redirectTo(signInResult.redirectTo);
        return;
      }

      if (signInError) {
        const errorCode = await getErrorCode(signInError);

        if (errorCode !== 'user.user_not_exist') {
          await handleError(signInError, {
            global: (error) => {
              setErrorMessage(error.message);
            },
          });
          setIsSubmitting(false);

          return;
        }

        const [registerError, registerResult] = await registerWithIdentifier(verificationId);

        if (registerError) {
          await handleError(registerError, {
            global: (error) => {
              setErrorMessage(error.message);
            },
          });
          setIsSubmitting(false);

          return;
        }

        if (registerResult?.redirectTo) {
          await redirectTo(registerResult.redirectTo);
        }
      }

      setIsSubmitting(false);
    },
    [
      code,
      handleError,
      identifier,
      identifyWithCode,
      redirectTo,
      registerWithIdentifier,
      validateAgreement,
      validatePhoneInput,
      verificationId,
      t,
    ]
  );

  const isPhoneValid = isValidEvaPhoneNumber(phone);
  const isCodeValid = code.length === verificationCodeLength;
  const isSendCodeDisabled = isSendingCode || isRunning || !isPhoneValid;
  const isBusy = isSendingCode || isSubmitting;
  const isSubmitDisabled = isBusy || !isPhoneValid || !isCodeValid;
  const sendCodeText = isRunning
    ? t('eva_domestic.countdown', { seconds })
    : isSendingCode
      ? t('eva_domestic.sending')
      : verificationId
        ? t('eva_domestic.resend')
        : t('eva_domestic.send_code');

  return (
    <form className={isEmbedded ? styles.embedded : styles.card} onSubmit={handleSubmit}>
      {!isEmbedded && (
        <div className={styles.header}>
          <EvaAudioLogo
            className={styles.logo}
            role="img"
            aria-label="EVA Audio"
            data-testid="eva-logo"
          />
          <h1 className={styles.subtitle}>{t('eva_domestic.welcome')}</h1>
        </div>
      )}

      <div className={styles.fields}>
        <EvaInput
          prefix="+86"
          name="phone"
          autoComplete="tel-national"
          inputMode="numeric"
          placeholder={t('eva_domestic.phone_placeholder')}
          value={phone}
          onChange={(event) => {
            setPhone(event.currentTarget.value.replaceAll(/\D/g, '').slice(0, 11));
          }}
        />

        <div className={styles.verificationField}>
          <EvaInput
            name="verificationCode"
            autoComplete="one-time-code"
            inputMode="numeric"
            placeholder={t('eva_domestic.code_placeholder')}
            value={code}
            suffix={
              <button
                className={classNames(styles.codeButton, isRunning && styles.codeButtonCountdown)}
                disabled={isSendCodeDisabled}
                type="button"
                onClick={handleSendCode}
              >
                {sendCodeText}
              </button>
            }
            onChange={(event) => {
              setCode(
                event.currentTarget.value.replaceAll(/\D/g, '').slice(0, verificationCodeLength)
              );
            }}
          />

          {errorMessage && <div className={styles.error}>{errorMessage}</div>}
        </div>

        <CaptchaBox />

        <EvaAgreement className={styles.agreement} isShaking={isAgreementShaking} />
      </div>

      <button className={styles.submit} disabled={isSubmitDisabled} type="submit">
        {isSubmitting ? (
          <>
            <LoaderCircle
              aria-hidden
              className={styles.submitLoader}
              data-testid="submit-loader"
              size={20}
            />
            <span>{t('eva_domestic.signing_in')}</span>
          </>
        ) : (
          t('eva_domestic.sign_in_or_register')
        )}
      </button>
    </form>
  );
};

export default EvaPhoneCodeSignIn;
